import { useEffect, useMemo, useState } from 'react';
import { EventContext, EventContextValues } from '@hooks/useEventContext';
import { isEventAdmin } from '@utils/shared-utils';
import useUser from '@hooks/useUser';
import {
    fetchEventWithMembership,
    fetchPublicEvent,
    getEventStripeId,
} from '@modules/supabase/events';
import { useParams } from 'react-router';
import useLocationQuery from '@hooks/useLocationQuery';
import { logError } from '@utils/logging';
import useWatchStripeAccount from '@hooks/useWatchStripeAccount';

export default function EventProvider({
    children,
    event: initialEvent,
}: {
    children: JSX.Element;
    event?: EventContextValues['event'];
}): React.JSX.Element {
    const [event, setEvent] = useState<EventContextValues['event']>(initialEvent || null);
    const [stripePaymentUser, setStripePaymentUser] = useState<{
        stripeId: string;
        userId: string;
    } | null>(null);
    const stripeState = useWatchStripeAccount(
        stripePaymentUser?.userId,
        `EventProvider ${event?.id}`,
    );

    const { loading: userLoading, user } = useUser();
    const params = useParams<{ eventId: string | undefined }>();
    const query = useLocationQuery();

    // get eventId from router url
    const urlEventId = query.get('eventId') || params.eventId;
    const eventId = urlEventId || event?.id;

    const registrationCount = event?.registrantsCount || 0;

    const userEventMembership = event && 'membership' in event ? event?.membership : undefined;

    useEffect(() => {
        if (eventId) {
            if (event?.id !== eventId) {
                // clear event if it's not the same as the one we're looking for
                setEvent(null);
                (user?.id ? fetchEventWithMembership(user.id, eventId) : fetchPublicEvent(eventId))
                    .then((res: EventContextValues['event']) => setEvent(res))
                    .catch(logError);
            }
            getEventStripeId(eventId).then(setStripePaymentUser).catch(logError);
        } else {
            setEvent(null);
        }
    }, [event?.id, eventId, user?.id]);

    const isAdmin = isEventAdmin(user, event?.createdByUserId, userEventMembership);

    // console.log('EventProvider', { eventId, isAdmin, event, userEventMembership });

    const eventContext: EventContextValues = useMemo(() => {
        return {
            loading: event === null || urlEventId !== event.id || userLoading,
            event,
            isEventAdmin: isAdmin,
            membership: userEventMembership,
            registrationCount,
            stripePaymentUser,
            stripeState,
        };
    }, [
        event,
        isAdmin,
        registrationCount,
        stripePaymentUser,
        stripeState,
        urlEventId,
        userEventMembership,
        userLoading,
    ]);

    return <EventContext.Provider value={eventContext}>{children}</EventContext.Provider>;
}
